import { Link, useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { SerializedError } from '@reduxjs/toolkit';
import {
  formatCurrency,
  formatList,
} from 'renderer/shared/formatters/formatters';
import { useState, useEffect } from 'react';
import { useAppSelector } from 'renderer/shared/state/hooks';
import { selectUserRole } from 'renderer/features/auth/state/userSelectors';
import apiSlice from 'renderer/shared/state/apiSlice';
import { transform } from 'lodash';
import AddToCartConfirmationModal from './AddToCartConfirmationModal';
import {
  useGetProductDetailsQuery,
  useListProductsQuery,
} from '../services/products.endpoints';
import {
  useGetCartQuery,
  useAddToCartMutation,
} from '../../payments-page/services/cart.endpoints';

import { GetProductDetailsResponse } from '../schemas/products.schemas';
import productThumbnails from '../constants/productThumbnails';
import ProductDeleteDialog from './ProductDeleteDialog';
import PurchaseDialog from './PurchaseDialog';
import FormattedDescription from './FormattedDescription';

const LoadingCard = () => {
  return (
    <Grid container spacing={3}>
      <Grid item xs={4}>
        <Skeleton variant="rounded" data-testid="product-loading" />
      </Grid>
      <Grid item container spacing={3} direction="column" xs={8}>
        <Grid item>
          <Typography variant="h2">
            <Skeleton />
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant="h4">
            <Skeleton />
          </Typography>
        </Grid>
        <Grid item>
          <Skeleton />
        </Grid>
      </Grid>
      <Grid item>
        <Skeleton />
      </Grid>
    </Grid>
  );
};

interface ErrorCardProps {
  error: FetchBaseQueryError | SerializedError;
}

const ErrorCard = ({ error }: ErrorCardProps) => {
  return (
    <Card>
      <CardHeader title="Error loading product details" />
      <CardContent>
        <Typography>
          {'status' in error && `${error.status}: ${error.data}`}
        </Typography>
      </CardContent>
    </Card>
  );
};

interface ProductDetailsProps {
  details: GetProductDetailsResponse;
  id: string;
}

interface CartItem {
  productId: {
    _id: string;
    productId: string;
    name: string;
    fullPrice: number;
  };
  quantity: number;
  platform?: string;
  hostApp?: {
    id: string;
    displayName: string;
    version: string;
    _id?: string;
  };
}

const ProductDetails = ({ details, id }: ProductDetailsProps) => {
  const [hostAppError, setHostAppError] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const role = useAppSelector(selectUserRole);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null,
  );
  const [triggerAddToCartAfterDialog, setTriggerAddToCartAfterDialog] =
    useState(false);

  const [platform, setPlatform] = useState<string>('Windows');
  // const [hostApp, setHostApp] = useState<string>('');
  const [hostApp, setHostApp] = useState<{
    id: string;
    displayName: string;
    version: string;
  } | null>(null);

  const [quantity, setQuantity] = useState<number>(1);

  const [addToCart] = useAddToCartMutation();
  // const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [cartItems, setCartItems] = useState([]);
  const { refetch } = useGetCartQuery(null);

  // 🔁 Redirect logic - TEMPORARY FIX FOR CUSTOMERS GOING TO OLD FF2 LINK
  const restrictedProductId = '66341fef4895dadba053975d'; // FF2
  const newProductId = '67f6b5c241f8c9345e025d6d'; // FF3
  useEffect(() => {
    if ((role === 'user' || role === 'public') && id === restrictedProductId) {
      navigate(`/products/${newProductId}`, { replace: true });
    }
  }, [role, id, navigate]);

  const {
    data: products,
    isLoading,
    error,
  } = useListProductsQuery({ includeHidden: role === 'admin' });

  const {
    _id,
    name,
    fullDescription,
    briefDescription,
    productId,
    productPage,
    downloadPage,
    hidden,
    keywords,
    category,
    supportedHostApplications,
    supportedPlatforms,
    startingPrice,
    salePrice,
  } = details;
  const image = productThumbnails.get(productId) ?? '';
  const isOnSale = salePrice !== null && salePrice !== undefined;
  const isSubscription = category === 'Subscription';
  // const isOnSale = !!sale && new Date(sale.until) > new Date();

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [cartSummary, setCartSummary] = useState({
    productName: name,
    productImage: image,
    platform,
    hostApplication: hostApp,
    quantity,
    subtotal: 0,
    totalItems: 0,
  });

  useEffect(() => {
    if (supportedPlatforms?.length) setPlatform(supportedPlatforms[0]);
    if (supportedHostApplications?.length === 1) {
      setHostApp(supportedHostApplications[0]);
      setHostAppError(false); // clear any previous error
    }
  }, [supportedPlatforms, supportedHostApplications]);

  if (!id) return null;

  const handlePurchaseClick = async (
    pid: string,
    productCategory: string,
    productName: string,
  ) => {
    if (!hostApp) {
      setHostAppError(true);
      return;
    }
    if (
      productCategory === 'Video Plugin' &&
      productName.toLowerCase().indexOf('anarchy complete bundle') < 0
    ) {
      setSelectedProductId(pid);
      setModalOpen(true);
    } else {
      try {
        await addToCart({
          productId: pid,
          quantity,
          platform,
          hostApplication: hostApp,
        }).unwrap();
        // navigate('/checkout');
        navigate(`/cart/${pid}`);
      } catch (err) {
        console.error('Buy Now failed to add to cart', err);
        alert('Could not proceed to checkout.');
      }
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedProductId(null);
  };

  const handleAddToCart = async () => {
    if (!hostApp) {
      setHostAppError(true);
      return;
    }
    if (category === 'Video Plugin') {
      setSelectedProductId(_id);
      setTriggerAddToCartAfterDialog(true); // tells PurchaseDialog to behave differently
      setModalOpen(true);
      return;
    }

    setHostAppError(false);

    try {
      const updatedCart = await addToCart({
        productId: _id,
        quantity,
        platform,
        hostApplication: hostApp,
      }).unwrap();

      dispatch(apiSlice.util.invalidateTags([{ type: 'Cart' }]));

      const sortedItems = [
        ...updatedCart.items.filter((i: any) => i.productId === _id),
        ...updatedCart.items.filter((i: any) => i.productId !== _id),
      ];

      setCartSummary({
        productName: name,
        productImage: image,
        platform,
        hostApplication: hostApp,
        quantity,
        subtotal: (salePrice ?? startingPrice) * quantity,
        totalItems: sortedItems.reduce((sum, i) => sum + i.quantity, 0),
      });

      setCartItems(sortedItems as never[]);
      setConfirmationOpen(true);
    } catch (err) {
      console.error('Failed to add to cart', err);
    }
  };

  const handleNavigate = (path: string) => {
    setModalOpen(false);

    if (triggerAddToCartAfterDialog) {
      setTriggerAddToCartAfterDialog(false);

      // Use setTimeout to wait until the modal unmounts
      setTimeout(() => {
        handleAddToCart();
        navigate(path); // Navigate *after* adding
      }, 200);

      return;
    }

    navigate(path);
  };

  return (
    <>
      <Grid container spacing={4}>
        <Grid item xs={12} md={5}>
          <Tooltip title={keywords}>
            <Card
              data-testid="product-keywords"
              elevation={2}
              sx={{ borderRadius: 2 }}
            >
              <CardMedia
                // height={450}
                component="img"
                image={image}
                alt={name}
                data-testid="product-image"
                // sx={{ objectFit: 'contain', maxHeight: 400 }}
              />
              {role === 'admin' && (
                <CardActions>
                  <Box>
                    <Link to={`/products/edit/${id}`}>
                      <Button variant="contained" color="secondary">
                        Edit
                      </Button>
                    </Link>
                  </Box>
                  <Box>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => setDeleteDialogOpen(true)}
                    >
                      Delete
                    </Button>
                  </Box>
                  <ProductDeleteDialog
                    open={deleteDialogOpen}
                    setOpen={setDeleteDialogOpen}
                    id={id}
                  />
                </CardActions>
              )}
            </Card>
          </Tooltip>
        </Grid>

        {/* <Grid item xs={12} md={7}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            {name}
          </Typography>
          {hidden && (
            <Tooltip title="This item is hidden from customers.">
              <VisibilityOffIcon color='action' data-testid="product-hidden" />
            </Tooltip>
          )}

          <Typography variant="h6" color="text.secondary" gutterBottom>
            {isOnSale ? (
              <>
                <span
                  style={{ textDecoration: 'line-through', marginRight: 8 }}
                >
                  {formatCurrency(startingPrice)}
                </span>
                {formatCurrency(salePrice)}
              </>
            ) : (
              formatCurrency(startingPrice)
            )}
            {isSubscription && ' / year'}
          </Typography>
          <Typography fontWeight={500} gutterBottom sx={{ transform: 'lowercase' }}>
            {category}
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 3 }}>
            {briefDescription}
          </Typography>


          <FormControl
            size="small"
            fullWidth
            // sx={{ mb: 2 }}
            sx={{ mb: 2, backgroundColor: 'white', borderRadius: 1 }}
          >
            <InputLabel id="platform-label">Platform</InputLabel>
            <Select
              labelId="platform-label"
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              label="Platform"
            >
              {supportedPlatforms.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            size="small"
            fullWidth
            sx={{ mb: 2, backgroundColor: 'white', borderRadius: 1 }}
          >
            <InputLabel id="hostApp-label">Host Application</InputLabel>
            <Select
              labelId="hostApp-label"
              value={hostApp?.id ?? ''}
                onChange={(e) => {
                  const selected = supportedHostApplications.find(
                    (app) => app.id === e.target.value,
                  );
                  setHostApp(selected ?? null);
                  setHostAppError(!selected);
                }}
              label="Host App"
            >
              {supportedHostApplications.map((app) => (
                <MenuItem key={app.id} value={app.id}>
                  {app.displayName}
                </MenuItem>
              ))}
            </Select>

            {hostAppError && (
                <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                  Please select a host application.
                </Typography>
              )}
          </FormControl>

          <TextField
            label="Quantity"
            type="number"
            fullWidth
            size="small"
            sx={{ mb: 3, backgroundColor: 'white', borderRadius: 1 }}
            inputProps={{ min: 1 }}
            value={quantity}
            onChange={(e) => setQuantity(Number(e.target.value))}
          />

          <Tooltip
            title={hostApp ? '' : 'Please select a host application'}
            placement="top"
            disableHoverListener={!!hostApp}
          >

            <Button  onClick={(e) => {
              e.preventDefault();
              handlePurchaseClick(_id, category, name);
            }} fullWidth variant="contained" size="large" sx={{ mb: 2 }}>
              Buy Now
            </Button>
          </Tooltip>

          <Tooltip
            title={hostApp ? '' : 'Please select a host application'}
            placement="top"
            disableHoverListener={!!hostApp}
          >
            <Button
              fullWidth
              variant="contained"
              size="large"
              sx={{
                backgroundColor: '#e0e0e0',
                color: '#212121',
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: '#d5d5d5',
                },
              }}
              onClick={handleAddToCart}
            >
              Add to Cart
            </Button>
          </Tooltip>
        </Grid> */}
        <Grid item xs={12} md={7}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            {name}
          </Typography>

          {hidden && (
            <Tooltip title="This item is hidden from customers.">
              <VisibilityOffIcon color="action" data-testid="product-hidden" />
            </Tooltip>
          )}

          <Typography variant="h6" color="text.secondary" gutterBottom>
            {isOnSale ? (
              <>
                <span
                  style={{ textDecoration: 'line-through', marginRight: 8 }}
                >
                  {formatCurrency(startingPrice)}
                </span>
                {formatCurrency(salePrice)}
              </>
            ) : (
              formatCurrency(startingPrice)
            )}
            {isSubscription && ' / year'}
          </Typography>

          <Typography
            variant="subtitle1"
            sx={{ mb: 3, textTransform: 'capitalize' }}
          >
            {category}
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 3 }}>
            {briefDescription}
          </Typography>

          {/* FORM SECTION */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: 'white', borderRadius: 1 }}
                >
                  <InputLabel id="platform-label">Platform</InputLabel>
                  <Select
                    labelId="platform-label"
                    value={platform}
                    onChange={(e) => setPlatform(e.target.value)}
                    label="Platform"
                  >
                    {supportedPlatforms.map((p) => (
                      <MenuItem key={p} value={p}>
                        {p}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  size="small"
                  margin="dense"
                  sx={{ backgroundColor: 'white', borderRadius: 1 }}
                >
                  <InputLabel id="hostApp-label">Host Application</InputLabel>
                  <Select
                    labelId="hostApp-label"
                    value={hostApp?.id ?? ''}
                    onChange={(e) => {
                      const selected = supportedHostApplications.find(
                        (app) => app.id === e.target.value,
                      );
                      setHostApp(selected ?? null);
                      setHostAppError(!selected);
                    }}
                    label="Host Application"
                  >
                    {supportedHostApplications.map((app) => (
                      <MenuItem key={app.id} value={app.id}>
                        {app.displayName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {hostAppError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    Please select a host application.
                  </Alert>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Quantity"
                  type="number"
                  fullWidth
                  size="small"
                  margin="dense"
                  inputProps={{ min: 1 }}
                  sx={{ backgroundColor: 'white', borderRadius: 1 }}
                  value={quantity}
                  onChange={(e) => setQuantity(Number(e.target.value))}
                />
              </Grid>

              <Grid item xs={12}>
                <Stack direction="row" spacing={2}>
                  <Button
                    onClick={(e) => {
                      e.preventDefault();
                      handlePurchaseClick(_id, category, name);
                    }}
                    variant="contained"
                    size="large"
                    sx={{ flex: 1 }}
                    disabled={!hostApp}
                  >
                    Buy Now
                  </Button>

                  <Button
                    onClick={handleAddToCart}
                    variant="contained"
                    size="large"
                    sx={{
                      flex: 1,
                      backgroundColor: '#e0e0e0',
                      color: '#212121',
                      fontWeight: 'bold',
                      '&:hover': { backgroundColor: '#d5d5d5' },
                    }}
                    disabled={!hostApp}
                  >
                    Add to Cart
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <Box mt={4}>
        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
          {fullDescription}
        </Typography>

        <Typography sx={{ mt: 2 }}>
          Need more info and examples? Check out the{' '}
          <Link to={productPage} target="_blank" rel="noopener noreferrer">
            Product Page
          </Link>
        </Typography>

        <Typography>
          Want to give it a try?{' '}
          <Link to={downloadPage} target="_blank" rel="noopener noreferrer">
            Download a copy
          </Link>
          .
        </Typography>

        <Typography>
          Supported on <b>{formatList(supportedPlatforms)}</b>.
        </Typography>

        <Typography sx={{ mb: 4 }}>
          Use it in{' '}
          <b>
            {formatList(
              supportedHostApplications.map((app) => app.displayName),
            )}
          </b>
          .
        </Typography>
      </Box>

      <AddToCartConfirmationModal
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        {...cartSummary}
        hostApp={
          cartSummary.hostApplication
            ? cartSummary.hostApplication.displayName
            : ''
        }
        items={cartItems}
      />

      <PurchaseDialog
        products={products as any[]}
        open={modalOpen}
        onClose={handleCloseModal}
        onNavigate={handleNavigate}
        productId={selectedProductId}
        hostApp={hostApp}
        platform={platform}
      />
    </>
  );
};

const ProductDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const role = useAppSelector(selectUserRole);
  const {
    data: details,
    error,
    isLoading,
  } = useGetProductDetailsQuery(id ?? '');

  return (
    <Container maxWidth="md" sx={{ p: 0 }}>
      <Button
        component={Link}
        to="/"
        startIcon={<ArrowBackIcon />}
        variant="text"
        sx={{ mb: 3 }}
      >
        Back to All Products
      </Button>

      {isLoading && <LoadingCard />}
      {error && <ErrorCard error={error} />}
      {details && <ProductDetails details={details} id={id ?? ''} />}
    </Container>
  );
};

export default ProductDetailsPage;
